import * as G from 'ApplicationEntry/models/ProductsRiders/PinCare/GroupPinCare';
import * as NY from 'ApplicationEntry/models/ProductsRiders/PinCare/NewYorkPinCare';
import * as O from 'ApplicationEntry/models/ProductsRiders/PinCare/OrdinaryPinCare';
import {matchPinCareByType, PinCare} from 'ApplicationEntry/models/ProductsRiders/PinCare/PinCare';
import {Async, Loading} from 'Core/models/Async';
import {NonEmptyString} from 'Core/models/EmptyString';
import {createAction} from 'Shared/utilities/actionCreator';
import {reverseEnum} from 'Shared/utilities/enumUtility';

export enum PinCareActionType {
  CHANGE_PACKAGE = 'pinCare/CHANGE_PACKAGE',
  CHANGE_PLAN_TYPE = 'pinCare/CHANGE_PLAN_TYPE',
  CHANGE_PREMIUM = 'pinCare/CHANGE_PREMIUM',
  CHANGE_CALCULATED_PREMIUM = 'pinCare/CHANGE_CALCULATED_PREMIUM',
  CHANGE_CWA = 'pinCare/CHANGE_CWA',
  CHANGE_REPLACEMENT = 'pinCare/CHANGE_REPLACEMENT',
  CHANGE_POLICY_NUMBER = 'pinCare/CHANGE_POLICY_NUMBER',
  CHANGE_UNITS = 'pinCare/CHANGE_UNITS',
  CHANGE_J_STAMP = 'pinCare/CHANGE_J_STAMP',
  CHANGE_SITUS_STATE = 'pinCare/CHANGE_SITUS_STATE',
  GET_GROUP_PIN_CARE_PRODUCTS = 'pinCare/GET_GROUP_PIN_CARE_PRODUCTS',
  CHANGE_GROUP_PIN_CARE_PRODUCTS = 'pinCare/CHANGE_GROUP_PIN_CARE_PRODUCTS',
  CHANGE_PACKAGE_ITEM = 'pinCare/CHANGE_PACKAGE_ITEM',
}

export const isPinCareAction = (action: any): action is PinCareAction => action.type in reverseEnum(PinCareActionType);

export const changePackage = (packageName: string) => createAction(PinCareActionType.CHANGE_PACKAGE, {packageName});
export const changePlanType = (planType: string) => createAction(PinCareActionType.CHANGE_PLAN_TYPE, {planType});
export const changePremium = (premium: string) => createAction(PinCareActionType.CHANGE_PREMIUM, {premium});
export const changeCalculatedPremium = (calculatedPremium: string) =>
  createAction(PinCareActionType.CHANGE_CALCULATED_PREMIUM, {calculatedPremium});
export const changeCWA = (cwa: string) => createAction(PinCareActionType.CHANGE_CWA, {cwa});
export const changeReplacement = (replacement: boolean) =>
  createAction(PinCareActionType.CHANGE_REPLACEMENT, {replacement});
export const changePolicyNumber = (policyNumber: string) =>
  createAction(PinCareActionType.CHANGE_POLICY_NUMBER, {policyNumber});
export const changeUnits = (units: string) => createAction(PinCareActionType.CHANGE_UNITS, {units});
export const changeJStamp = (jStamp: boolean) => createAction(PinCareActionType.CHANGE_J_STAMP, {jStamp});
export const changeSitusState = (situsState: string) =>
  createAction(PinCareActionType.CHANGE_SITUS_STATE, {situsState});
export const getGroupPinCareProducts = (caseNumber: string, planCode: string) =>
  createAction(PinCareActionType.GET_GROUP_PIN_CARE_PRODUCTS, {caseNumber, planCode});
export const changeGroupPinCareProducts = (packages: Async<object[]>) =>
  createAction(PinCareActionType.CHANGE_GROUP_PIN_CARE_PRODUCTS, {packages});
export const changePackageItem = (packageItem: object) =>
  createAction(PinCareActionType.CHANGE_PACKAGE_ITEM, {packageItem});

export type PinCareAction =
  | ReturnType<typeof changePackage>
  | ReturnType<typeof changePlanType>
  | ReturnType<typeof changePremium>
  | ReturnType<typeof changeCalculatedPremium>
  | ReturnType<typeof changeCWA>
  | ReturnType<typeof changeReplacement>
  | ReturnType<typeof changePolicyNumber>
  | ReturnType<typeof changeUnits>
  | ReturnType<typeof changeJStamp>
  | ReturnType<typeof changeSitusState>
  | ReturnType<typeof getGroupPinCareProducts>
  | ReturnType<typeof changeGroupPinCareProducts>
  | ReturnType<typeof changePackageItem>;

type PinCareState = PinCare;

export default (state: PinCareState, action: PinCareAction): PinCareState => {
  switch (action.type) {
    case PinCareActionType.CHANGE_PACKAGE:
      return matchPinCareByType<PinCare>({
        Ordinary: O.changePackage(action.payload.packageName),
        Group: G.changePackage(action.payload.packageName),
        NewYork: NY.changePackage(action.payload.packageName),
      })(state);
    case PinCareActionType.CHANGE_PLAN_TYPE:
      return matchPinCareByType<PinCare>({
        Ordinary: O.changePlanType(action.payload.planType),
        Group: G.changePlanType(action.payload.planType),
        NewYork: NY.changePlanType(action.payload.planType),
      })(state);
    case PinCareActionType.CHANGE_PREMIUM:
      return matchPinCareByType<PinCare>({
        Ordinary: O.changePremium(action.payload.premium),
        Group: G.changePremium(action.payload.premium),
        NewYork: NY.changePremium(action.payload.premium),
      })(state);
    case PinCareActionType.CHANGE_CALCULATED_PREMIUM:
      return matchPinCareByType<PinCare>({
        Ordinary: O.changeCalculatedPremium(action.payload.calculatedPremium),
        Group: G.changeCalculatedPremium(action.payload.calculatedPremium),
        NewYork: NY.changeCalculatedPremium(action.payload.calculatedPremium),
      })(state);
    case PinCareActionType.CHANGE_CWA:
      return matchPinCareByType<PinCare>({
        Ordinary: O.changeCWA(action.payload.cwa),
        Group: G.changeCWA(action.payload.cwa),
        NewYork: NY.changeCWA(action.payload.cwa),
      })(state);
    case PinCareActionType.CHANGE_REPLACEMENT:
      return matchPinCareByType<PinCare>({
        Ordinary: O.changeReplacement(action.payload.replacement),
        Group: G.changeReplacement(action.payload.replacement),
        NewYork: NY.changeReplacement(action.payload.replacement),
      })(state);
    case PinCareActionType.CHANGE_J_STAMP:
      return matchPinCareByType<PinCare>({
        Ordinary: O.changeJStamp(action.payload.jStamp),
        Group: G.changeJStamp(action.payload.jStamp),
        NewYork: NY.changeJStamp(action.payload.jStamp),
      })(state);
    case PinCareActionType.CHANGE_POLICY_NUMBER:
      return matchPinCareByType<PinCare>({
        Ordinary: O.changePolicyNumber(NonEmptyString.create(action.payload.policyNumber)),
        Group: G.changePolicyNumber(NonEmptyString.create(action.payload.policyNumber)),
        NewYork: NY.changePolicyNumber(NonEmptyString.create(action.payload.policyNumber)),
      })(state);
    case PinCareActionType.CHANGE_UNITS:
      return matchPinCareByType<PinCare>({
        Ordinary: O.changeUnits(action.payload.units),
        Group: G.changeUnits(action.payload.units),
        NewYork: NY.changeUnits(action.payload.units),
      })(state);
    case PinCareActionType.CHANGE_SITUS_STATE:
      return matchPinCareByType<PinCare>({
        Ordinary: O.changeSitusState(action.payload.situsState),
        Group: G.changeSitusState(action.payload.situsState),
        NewYork: NY.changeSitusState(action.payload.situsState),
      })(state);
    case PinCareActionType.GET_GROUP_PIN_CARE_PRODUCTS:
      return matchPinCareByType<PinCare>({
        Group: G.changePackageList(new Loading()),
        _: () => state,
      })(state);
    case PinCareActionType.CHANGE_GROUP_PIN_CARE_PRODUCTS:
      return matchPinCareByType<PinCare>({
        Group: G.changePackageList(action.payload.packages),
        _: () => state,
      })(state);
    case PinCareActionType.CHANGE_PACKAGE_ITEM:
      return matchPinCareByType<PinCare>({
        Group: G.changePackageItem(action.payload.packageItem),
        _: () => state,
      })(state);
    default:
      return state;
  }
};
